/**
 * Contrato dos documentos pessoais da família.
 *
 * Papel: aqui ficam `STATUS_DOCUMENTO`, de onde nasce o enum `status_documento`
 * do Postgres, e os schemas que o formulário e as Functions usam —
 * `novoDocumentoSchema` na entrada, `documentoSchema` na saída.
 *
 * Como usar: `tipo` é o nome do documento como se escreve, digitado livre —
 * `Passaporte BRA`, `CIN`, `Visto EUA` —, e não uma escolha entre valores fixos.
 * Uma pessoa pode ter quantos documentos quiser, inclusive dois com o mesmo
 * `tipo`, como quem tem dupla cidadania e guarda dois passaportes. Quem
 * precisar agrupar por tipo deverá comparar o texto gravado, sabendo que
 * `Passaporte` e `Passaporte BRA` são, para o banco, dois tipos diferentes.
 *
 * `status` diz respeito à posse do documento, e não à vigência: quem responde
 * pela vigência é `validade`, e as duas podem discordar. Um documento marcado
 * `valido` cuja `validade` já passou continua `valido` até alguém corrigir, e é
 * a tela que põe a marca de vencido ao lado, vinda da data.
 */
import { z } from 'zod';

import { dataOpcionalSchema, idSchema, textoOpcional } from './comum';

/**
 * O nome do documento, como a tela o mostra.
 *
 * O vazio é recusado com a mesma frase do formulário de origem, porque a linha
 * sem `tipo` não diria de que documento são o número e as datas. O teto de 120
 * caracteres é o de `numero`: cabe `Passaporte italiano` com folga, e recusa o
 * texto colado por engano no campo errado.
 */
const tipoDocumentoSchema = z
  .string()
  .trim()
  .min(1, { message: 'Preencha ao menos o tipo de documento.' })
  .max(120, { message: 'O tipo de documento deve ter no máximo 120 caracteres.' });

/**
 * Em que pé está a posse do documento.
 *
 * `nao_localizado` é um valor só para as duas grafias que o cadastro de origem
 * usa — `Não localizado` e `Não localizada`, conforme o gênero do documento.
 * Separá-las aqui faria toda contagem por status trazer duas linhas para o
 * mesmo estado; a tela escreve sempre `Não localizado`, como a lista de status
 * do formulário de origem.
 *
 * `sem_informacao` é gravado, e não representado por `null`, porque é resposta
 * e não ausência: quem preenche a ficha está declarando que não sabe. Com
 * `null`, toda consulta teria de testar os dois casos para dizer a mesma coisa.
 */
export const STATUS_DOCUMENTO = [
  'valido',
  'nao_localizado',
  'nao_aplicavel',
  'sem_informacao',
] as const;

/** Em que pé está a posse do documento. */
export const statusDocumentoSchema = z.enum(STATUS_DOCUMENTO, {
  message: 'Status de documento desconhecido.',
});

export type StatusDocumento = z.infer<typeof statusDocumentoSchema>;

/**
 * O que o formulário envia para criar ou alterar um documento.
 *
 * `numero` guarda o que está no documento por inteiro, com o órgão emissor, a
 * categoria da habilitação ou a classificação do visto dentro dele — é assim
 * que o dado existe, e separá-los daria três colunas que se aplicam a um tipo
 * de documento cada e ficariam nulas na maior parte das linhas.
 *
 * A validade anterior à emissão é recusada: é erro de digitação, e passa
 * despercebida por anos num cadastro que ninguém relê.
 */
export const novoDocumentoSchema = z
  .object({
    pessoaId: idSchema,
    tipo: tipoDocumentoSchema,
    numero: textoOpcional(120),
    emissao: dataOpcionalSchema,
    validade: dataOpcionalSchema,
    status: statusDocumentoSchema,
    observacao: textoOpcional(2000),
  })
  .refine(
    (documento) =>
      documento.emissao === null ||
      documento.validade === null ||
      documento.validade >= documento.emissao,
    { message: 'A validade não pode ser anterior à emissão.', path: ['validade'] },
  );

export type NovoDocumento = z.infer<typeof novoDocumentoSchema>;

/** A pessoa dona do documento, como a listagem a traz aninhada. */
export const pessoaDoDocumentoSchema = z.object({
  id: idSchema,
  nomeCompleto: z.string(),
  apelido: z.string(),
});

/**
 * Um documento como a API o devolve.
 *
 * A pessoa vem aninhada, e não apenas como `pessoaId`, porque a tabela mostra o
 * nome em toda linha — sem ela, a tela faria uma segunda consulta a
 * `/api/pessoas-familia` só para traduzir os identificadores.
 */
export const documentoSchema = z.object({
  id: idSchema,
  pessoa: pessoaDoDocumentoSchema,
  tipo: z.string(),
  numero: z.string().nullable(),
  emissao: z.string().nullable(),
  validade: z.string().nullable(),
  status: statusDocumentoSchema,
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Documento = z.infer<typeof documentoSchema>;
